/* eslint-disable react-hooks/exhaustive-deps */
import { MajorTest } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import dateFormat from 'dateformat';
import { kebabCase } from 'lodash';
import { useEffect, useState } from 'react';
import { toId, unwrapCollection } from '../shared/utils';
import { AssignmentResourceFilter, isAllowedId } from './assignmentVisibility';
import { MAJOR_TEST_CATALOG } from './constants';

export interface TestResultRow {
  id: number;
  name: string;
  date: string;
  resultPath: string;
  viewPath?: string;
}

const readName = (item: any) =>
  item?.name ||
  item?.title ||
  item?.attributes?.name ||
  item?.attributes?.title ||
  (item?.id ? `Test ${item.id}` : '');

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return dateFormat(date, 'dd/mm/yyyy, HH:MM');
};

export const useTestResults = (
  user: any,
  assignmentFilter?: AssignmentResourceFilter | null,
  assignmentFilterReady = true
) => {
  const token = getFromLS('userToken') || '';
  const userId = Number(user?.id);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [majorRows, setMajorRows] = useState<TestResultRow[]>([]);
  const [minorRows, setMinorRows] = useState<TestResultRow[]>([]);

  useEffect(() => {
    if (!userId) {
      return undefined;
    }
    if (!assignmentFilterReady) {
      setLoading(true);
      return undefined;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const resultsRes = await MajorTest.getUserAllTestLatestResult(token, userId);
        if (cancelled) {
          return;
        }

        const payload = resultsRes?.data?.data || resultsRes?.data || resultsRes;
        const nextMajor: TestResultRow[] = MAJOR_TEST_CATALOG.filter(
          config =>
            payload?.[config.resultKey]?.id &&
            isAllowedId(assignmentFilter?.majorTestIds, config.id)
        ).map(config => {
          const result = payload[config.resultKey];
          return {
            id: Number(result.id),
            name: config.name,
            date: formatDateTime(result?.createdAt || result?.updatedAt),
            resultPath: config.resultPath(result.id),
          };
        });

        const nextMinor: TestResultRow[] = unwrapCollection(payload?.customTestResults).flatMap(
          (result: any) => {
            const resultId = toId(result);
            const testId = toId(result?.test);
            if (!resultId || !isAllowedId(assignmentFilter?.minorTestIds, testId)) {
              return [];
            }
            const name = String(readName(result?.test) || (testId ? `Test ${testId}` : 'Test'));
            const row: TestResultRow = {
              id: resultId,
              name,
              date: formatDateTime(result?.createdAt || result?.updatedAt),
              resultPath: `/mes-resultats/${resultId}/${userId}`,
            };
            if (testId) {
              row.viewPath = `/test/${testId}/${kebabCase(name)}`;
            }
            return [row];
          }
        );

        if (!cancelled) {
          setMajorRows(nextMajor);
          setMinorRows(nextMinor);
        }
      } catch {
        if (!cancelled) {
          setMajorRows([]);
          setMinorRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, token, assignmentFilter, assignmentFilterReady]);

  const term = search.trim().toLowerCase();
  const filterRows = (rows: TestResultRow[]) =>
    term ? rows.filter(row => `${row.name} ${row.date}`.toLowerCase().includes(term)) : rows;

  return {
    loading,
    search,
    setSearch,
    majorRows: filterRows(majorRows),
    minorRows: filterRows(minorRows),
    hasAnyResult: majorRows.length > 0 || minorRows.length > 0,
    compassPath: `/admin/${userId}/boussole/${new Date().getFullYear()}`,
  };
};
